import { DateTime } from "luxon";

import { RateDifferentialCustomFieldTypeValues } from "@miter/types/common";
import type { CustomFieldType, SafetyPassFailResult, SafetyPassFailValue } from "@miter/types/common";
import type { CustomField, CustomFieldValue, FormAnswer, FormField } from "@miter/types/models";

const PASS_FAIL_LABELS: Record<SafetyPassFailResult, string> = { pass: "Pass", fail: "Fail", na: "N/A" };

const isSafetyPassFailResult = (value: unknown): value is SafetyPassFailResult =>
  typeof value === "string" && Object.hasOwn(PASS_FAIL_LABELS, value);

const isSafetyPassFailValue = (value: unknown): value is SafetyPassFailValue =>
  typeof value === "object" && value !== null && "result" in value && isSafetyPassFailResult(value.result);

const formatSafetyPassFailValue = (value: SafetyPassFailValue): string => {
  const parts: string[] = [PASS_FAIL_LABELS[value.result] ?? value.result];
  if (value.notes) parts.push(value.notes);
  if (value.photo_ids?.length) {
    parts.push(`${value.photo_ids.length} photo${value.photo_ids.length > 1 ? "s" : ""}`);
  }
  return parts.join(" — ");
};

/** Falls back to the deprecated `options` array for older fields, which may store `options_list` as empty. */
export const getCustomFieldSelectOptionValues = (
  customField: Pick<CustomField, "options_list" | "options">
): string[] => {
  if (customField.options_list?.length) return customField.options_list.map((option) => option.value);
  return customField.options ?? [];
};

const RATE_DIFFERENTIAL_TRIGGER_FIELD_TYPES: ReadonlySet<CustomFieldType> = new Set(
  RateDifferentialCustomFieldTypeValues
);

export const isRateDifferentialTriggerCustomField = (
  customField: Pick<CustomField, "archived" | "parent_type" | "type">
): boolean =>
  !customField.archived &&
  customField.parent_type === "timesheet" &&
  RATE_DIFFERENTIAL_TRIGGER_FIELD_TYPES.has(customField.type);

type GetCustomFieldStringValueParams = {
  // Id-agnostic: `_id` is never read, so backend (ObjectId) callers can pass their model as-is.
  customField: Omit<CustomField, "_id"> | FormField;
  customFieldValue: CustomFieldValue | Pick<FormAnswer<string>, "value"> | undefined;
};

export const getCustomFieldStringValue = ({
  customField,
  customFieldValue,
}: GetCustomFieldStringValueParams): string => {
  const value = customFieldValue?.value;
  if (!customFieldValue || value === undefined || value === null) return "-";

  if (customField.type === "checkbox") {
    if (value === false || value === "false") return "No";
    else if (customFieldValue) return "Yes";
  }

  if (customField.type === "date") {
    if (value) return DateTime.fromISO(value as string).toLocaleString(DateTime.DATE_MED);
    else return "-";
  }

  if (customField.type === "datetime") {
    if (value) return DateTime.fromISO(value as string).toLocaleString(DateTime.DATETIME_MED);
    else return "-";
  }

  let optionsList: FormField["options"] | CustomField["options_list"] = [];
  if ("options_list" in customField) {
    optionsList = customField.options_list;
  } else if ("options" in customField) {
    optionsList = customField.options as FormField["options"];
  }

  if (customField.type === "select" && !customField.multiple && "_id" in customFieldValue) {
    const option = optionsList?.find((o) => {
      if (typeof o === "string" && o === customFieldValue.value?.toString()) return true;
      if (typeof o === "object" && "_id" in o && o._id === customFieldValue.value) return true;
      if (typeof o === "object" && "value" in o && o.value === customFieldValue.value?.toString())
        return true;
      return false;
    });
    if (typeof option === "string") return option;
    else if (typeof option === "object" && "value" in option) return option.value;
    else return "-";
  }

  if (customField.type === "select" && customField.multiple && Array.isArray(customFieldValue.value)) {
    const options = optionsList?.filter((o) => {
      if (typeof o === "string" && (customFieldValue.value as Array<string>)?.find((ans) => ans === o))
        return true;
      if (
        typeof o === "object" &&
        "_id" in o &&
        (customFieldValue.value as Array<string>)?.find((ans) => ans === o._id)
      )
        return true;
      if (
        typeof o === "object" &&
        "value" in o &&
        (customFieldValue.value as Array<string>)?.find((ans) => ans === o.value)
      )
        return true;
      return false;
    });

    const cleanedOptions = options?.map((option) => {
      if (typeof option === "string") return option;
      else if (typeof option === "object" && "value" in option) return option.value;
      else return "-";
    });

    return cleanedOptions?.join(", ") ?? "-";
  }

  // Set to an empty string if esignature because we will save the actual esignature data in a separate call
  if (customField.type === "esignature" && value) {
    return "";
  }

  if (customField.type === "safety_pass_fail" && isSafetyPassFailValue(value)) {
    return formatSafetyPassFailValue(value);
  }

  if (typeof value === "string") return value || "-";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "-";
};
